import { useState } from "react";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import { useTranslation } from "react-i18next";
import { PageContainer } from "../../../components/primitives/layout/PageContainer";
import { PageHeader } from "../../../components/primitives/layout/PageHeader";
import { LoadingState } from "../../../components/primitives/feedback/LoadingState";
import { ConfirmDialog } from "../../../components/primitives/feedback/ConfirmDialog";
import { Note } from "../../../components/primitives/data-display/Note";
import { gaps } from "../../../components/primitives/spacing";
import { FieldPicker } from "../../../components/domain/inputs/FieldPicker";
import { useInsightEditor, TITLE_WORD_LIMIT, BODY_WORD_LIMIT } from "./useInsightEditor";

/** Insight authoring form for both create and edit modes. */
export default function InsightEditor() {
  const { t } = useTranslation();
  const {
    isEditMode,
    isPublishedWithDraft,
    title,
    body,
    fields,
    titleWordCount,
    bodyWordCount,
    savingDraft,
    publishing,
    saveError,
    publishError,
    loading,
    canSave,
    canPublish,
    titleHasLinks,
    bodyHasLinks,
    authorizedFields,
    setTitle,
    setBody,
    setFields,
    handleSaveDraft,
    handlePublish,
    handleCancel,
  } = useInsightEditor();

  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);

  const titleOver = titleWordCount > TITLE_WORD_LIMIT;
  const bodyOver = bodyWordCount > BODY_WORD_LIMIT;

  if (loading)
    return (
      <PageContainer center>
        <LoadingState />
      </PageContainer>
    );

  return (
    <PageContainer>
      <Stack gap={gaps.base}>
        <PageHeader
          title={
            isEditMode ? t("expertInsights.editor.titleEdit") : t("expertInsights.editor.titleNew")
          }
        />

        <Note>{t("expertInsights.editor.onePointNote")}</Note>

        {isPublishedWithDraft && <Note>{t("expertInsights.editor.newVersionNote")}</Note>}

        <Stack gap={gaps.base}>
          <TextField
            label={t("expertInsights.editor.titleField")}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            error={titleOver || titleHasLinks}
            helperText={
              titleHasLinks
                ? t("expertInsights.linksNotAllowed")
                : t("expertInsights.editor.wordCount", {
                    count: titleWordCount,
                    max: TITLE_WORD_LIMIT,
                  })
            }
            fullWidth
            inputProps={{ "aria-label": t("expertInsights.editor.titleField") }}
          />

          <TextField
            label={t("expertInsights.editor.bodyField")}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            error={bodyOver || bodyHasLinks}
            helperText={
              bodyHasLinks
                ? t("expertInsights.linksNotAllowed")
                : t("expertInsights.editor.wordCount", {
                    count: bodyWordCount,
                    max: BODY_WORD_LIMIT,
                  })
            }
            multiline
            rows={8}
            fullWidth
            inputProps={{ "aria-label": t("expertInsights.editor.bodyField") }}
          />

          <FieldPicker
            value={fields}
            onChange={setFields}
            allowedUrns={authorizedFields}
            label={t("expertInsights.editor.fieldsLabel")}
            disabled={savingDraft || publishing}
          />
        </Stack>

        {saveError && <Note color="error">{saveError}</Note>}

        <Stack direction="row" gap={gaps.base} sx={{ justifyContent: "flex-end" }}>
          <Button variant="outlined" onClick={handleCancel} disabled={savingDraft || publishing}>
            {t("expertInsights.editor.cancel")}
          </Button>
          <Button
            variant="contained"
            color="inherit"
            onClick={() => void handleSaveDraft()}
            disabled={!canSave}
          >
            {savingDraft ? t("expertInsights.editor.saving") : t("expertInsights.editor.saveDraft")}
          </Button>
          <Tooltip
            title={!canPublish && canSave ? t("expertInsights.editor.publishNoFieldsTooltip") : ""}
          >
            <span>
              <Button
                variant="contained"
                onClick={() => setPublishConfirmOpen(true)}
                disabled={!canPublish}
              >
                {t("expertInsights.editor.publish")}
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      <ConfirmDialog
        open={publishConfirmOpen}
        title={t("expertInsights.editor.publishConfirmTitle")}
        message={t("expertInsights.editor.publishConfirmMessage")}
        confirmLabel={t("expertInsights.editor.publish")}
        loading={publishing}
        error={publishError}
        onConfirm={() => void handlePublish()}
        onCancel={() => {
          if (!publishing) setPublishConfirmOpen(false);
        }}
      />
    </PageContainer>
  );
}
